import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Cookie } from "@phosphor-icons/react";
import { getConsent, grantConsent, denyConsent } from "@/lib/analytics";

// Lightweight GDPR/CCPA cookie-consent banner. Tracking stays disabled
// (Consent Mode denied + Pixel revoked) until the visitor accepts.
export default function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Don't include the banner in the react-snap prerender snapshot — keeps
    // the static HTML identical to the client's first render (clean hydration).
    if (typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap") return;
    if (!getConsent()) setVisible(true);
  }, []);

  const accept = () => {
    grantConsent();
    setVisible(false);
  };
  const decline = () => {
    denyConsent();
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="consent-banner"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="fixed z-[60] bottom-0 left-0 right-0 md:bottom-6 md:right-6 md:left-auto md:max-w-md bg-sand/97 backdrop-blur-xl border-t border-emerald/15 md:border rounded-t-2xl md:rounded-2xl md:shadow-2xl px-4 py-3 md:p-6"
          role="dialog"
          aria-label="Cookie consent"
        >
          <div className="flex items-center gap-3 md:flex-col md:items-stretch">
            {/* Desktop: full card header */}
            <div className="hidden md:flex items-start gap-3">
              <span className="w-10 h-10 rounded-full bg-sunset/12 grid place-items-center shrink-0">
                <Cookie size={20} weight="duotone" className="text-sunset" />
              </span>
              <div>
                <p className="font-display text-xl text-emerald leading-tight mb-1.5">We value your privacy</p>
                <p className="text-sm text-emerald-soft leading-relaxed">
                  We use cookies to understand site traffic and improve your experience. You can accept
                  or decline analytics &amp; marketing cookies — essential site features work either way.{" "}
                  <Link to="/privacy" data-testid="consent-learn-more" className="text-sunset underline underline-offset-2 hover:text-emerald transition-colors duration-300">Learn more</Link>
                </p>
              </div>
            </div>

            {/* Mobile: compact one-line text */}
            <p className="md:hidden flex-1 text-xs text-emerald-soft leading-snug pr-1">
              We use cookies to improve your experience.{" "}
              <Link to="/privacy" className="text-sunset underline underline-offset-2">Learn more</Link>
            </p>

            {/* Buttons */}
            <div className="flex gap-2 shrink-0 md:gap-3 md:mt-4 md:justify-end">
              <button
                data-testid="consent-decline"
                onClick={decline}
                className="rounded-full border border-emerald/20 px-4 py-2 text-xs font-medium text-emerald transition-colors duration-300 hover:bg-emerald hover:text-sand md:px-6 md:py-2.5 md:text-sm"
              >
                Decline
              </button>
              <button
                data-testid="consent-accept"
                onClick={accept}
                className="rounded-full bg-sunset px-4 py-2 text-xs font-medium text-sand transition-colors duration-300 hover:bg-emerald md:px-6 md:py-2.5 md:text-sm"
              >
                Accept<span className="hidden md:inline"> all</span>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
